// Ablaufaufzeichnung im Browser (08.10.2026) – Gegenstück zu backend/module_ablauf.
// Ein „Vorgang“ ist ein zusammenhängender Arbeitsschritt (z. B. „Angebot neu“), eine „Aktion“ ein
// einzelner Klick darin (z. B. „Speichern“). Jede Serveranfrage trägt die Kennungen in Kopfzeilen
// (X-Vorgang-Id, X-Aktion-Id, X-Aktion, X-Client-Version), damit Server und Browser zusammenpassen.
// Es werden nur Kennungen und feste Namen übertragen, keine Eingaben. Fehler hier stören nie die Arbeit.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AKTION_GUELTIG_MS = 5000; // eine Aktion gilt nur für Anfragen kurz nach dem Klick
const AUSLOESER_GUELTIG_MS = 10000;

let offen = []; // offene Vorgänge, innerster zuletzt
let letzteAktion = null; // { id, name, zeit, vorgangId }
let zuletztBeendet = null; // { id, zeit }

// ── Butler-Schalter (B-2) ──────────────────────────────────────────────────
// Der Butler ist beim Laden der Seite IMMER aus (nur Arbeitsspeicher, nichts gespeichert),
// darum ist er auch nach erneutem Öffnen der Anwendung wieder aus (Anforderungen 1 und 6).
// Erst „Butler aktivieren“ schaltet die Aufzeichnung an. Ohne das wird nichts gesendet und
// keine Butler-Kopfzeile gesetzt (Anforderungen 2 und 8, Browser-Seite).
let butlerAktiv = false;
const butlerHoerer = new Set(); // UI-Leiste meldet sich an und wird bei Änderung benachrichtigt
const butlerMelden = () => { butlerHoerer.forEach((f) => { try { f(butlerAktiv); } catch { /* egal */ } }); };

// Vorgänge dieser Butler-Sitzung (nur Kennung, fester Name, Zeit, Ergebnis) – für „Fehler gefunden“ (B-3).
const MAX_SITZUNGS_VORGAENGE = 30;
let sitzungsVorgaenge = [];
export const butlerVorgaenge = () => sitzungsVorgaenge.map((v) => ({ ...v }));

export const butlerIstAktiv = () => butlerAktiv;
export function butlerAktivieren() { sitzungsVorgaenge = []; butlerAktiv = true; butlerMelden(); }
export function butlerBeenden() {
  // offene Vorgänge sauber abschließen, dann abschalten
  try { [...offen].reverse().forEach((v) => beendeVorgang(v.id, "abgebrochen")); } catch { /* egal */ }
  offen = []; letzteAktion = null; zuletztBeendet = null;
  butlerAktiv = false; butlerMelden();
}
export function butlerBeobachten(f) { butlerHoerer.add(f); try { f(butlerAktiv); } catch { /* egal */ } return () => butlerHoerer.delete(f); }

const neueId = (p) => p + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

export const programmVersion = () => {
  try {
    const s = Array.from(document.scripts).find((x) => /\/static\/js\/main\.[^/]+\.js$/.test(x.src || ""));
    return s ? new URL(s.src).pathname.split("/").pop() : "entwicklung";
  } catch { return "unbekannt"; }
};

// Seitenadresse ohne Kennungen und ohne Suchtext (nur Vorlage)
const seite = () => {
  try {
    return window.location.pathname.replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ":id").replace(/\/[A-Za-z0-9_-]{20,}/g, "/:id");
  } catch { return null; }
};

const senden = (daten, { beimSchliessen = false } = {}) => {
  try {
    if (!butlerAktiv) return; // Butler aus: nichts senden (Anforderungen 2 und 8)
    const token = localStorage.getItem("token");
    if (!token || !BACKEND_URL) return;
    const inhalt = { ...daten, seite: seite(), version_client: programmVersion(), zeit_client: new Date().toISOString() };
    if (beimSchliessen && navigator.sendBeacon) {
      // Beim Schließen/Neuladen kommt nur sendBeacon zuverlässig an; es kann keine Kopfzeilen setzen,
      // daher stehen Anmeldung und Butler-Zustand im Inhalt (werden vom Server geprüft, nicht gespeichert).
      navigator.sendBeacon(`${BACKEND_URL}/api/ablauf/ereignis-beacon`, new Blob([JSON.stringify({ ...inhalt, token, butler: true })], { type: "text/plain" }));
      return;
    }
    fetch(`${BACKEND_URL}/api/ablauf/ereignis`, {
      method: "POST",
      keepalive: true, // kommt auch beim Schließen der Seite noch an
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "X-Butler-Aktiv": "1" },
      body: JSON.stringify(inhalt),
    }).catch(() => {});
  } catch { /* Aufzeichnung darf nie stören */ }
};

const sauber = (bezug) => {
  const out = {};
  for (const k of ["kunde_id", "projekt_id", "dokument_id", "dokument_art"]) if (bezug && bezug[k]) out[k] = String(bezug[k]);
  return out;
};

export const aktuellerVorgang = () => offen[offen.length - 1] || null;

export function starteVorgang(name, bezug = {}, { eltern } = {}) {
  try {
    if (!butlerAktiv) return null; // Butler aus: nichts erfassen
    const v = { id: neueId("v-"), name, bezug: sauber(bezug) };
    const elternId = eltern !== undefined ? eltern
      : aktuellerVorgang()?.id || (zuletztBeendet && Date.now() - zuletztBeendet.zeit < 5000 ? zuletztBeendet.id : null);
    const ausloeser = letzteAktion && Date.now() - letzteAktion.zeit < AUSLOESER_GUELTIG_MS ? letzteAktion.id : null;
    offen.push(v);
    sitzungsVorgaenge = [...sitzungsVorgaenge, { id: v.id, name, eltern: elternId || null, zeit: Date.now(), ergebnis: null }].slice(-MAX_SITZUNGS_VORGAENGE);
    senden({ typ: "vorgang_start", vorgang_id: v.id, eltern_id: elternId, ausloeser_aktion_id: ausloeser, name, bezug: v.bezug });
    return v.id;
  } catch { return null; }
}

export function aktion(name, bezug = {}) {
  try {
    if (!butlerAktiv) return; // Butler aus: nichts erfassen
    const v = aktuellerVorgang();
    letzteAktion = { id: neueId("k-"), name, zeit: Date.now(), vorgangId: v?.id || null };
    senden({ typ: "aktion", vorgang_id: v?.id || null, aktion_id: letzteAktion.id, name, bezug: sauber(bezug) });
  } catch { /* nie stören */ }
}

// ergebnis: gespeichert | ohne_aenderung | abgebrochen | seite_verlassen | fehler | ok
export function beendeVorgang(id, ergebnis, bezug = {}, { beimSchliessen = false } = {}) {
  try {
    if (!butlerAktiv) return; // Butler aus: nichts erfassen
    const v = offen.find((x) => x.id === id);
    if (!v) return;
    offen = offen.filter((x) => x.id !== id);
    zuletztBeendet = { id, zeit: Date.now() };
    sitzungsVorgaenge = sitzungsVorgaenge.map((x) => (x.id === id ? { ...x, ergebnis } : x));
    senden({ typ: "vorgang_ende", vorgang_id: id, name: v.name, ergebnis, bezug: sauber({ ...v.bezug, ...bezug }) }, { beimSchliessen });
  } catch { /* nie stören */ }
}

// Kopfzeilen für jede Serveranfrage (wird in lib/api.js verwendet)
// Butler aus: KEINE Butler-Kopfzeilen – der Server zeichnet dann nichts auf (Anforderung 8).
export function kopfzeilen() {
  if (!butlerAktiv) return {};
  const h = { "X-Butler-Aktiv": "1", "X-Client-Version": programmVersion() };
  try {
    const v = aktuellerVorgang();
    if (v) h["X-Vorgang-Id"] = v.id;
    if (letzteAktion && Date.now() - letzteAktion.zeit < AKTION_GUELTIG_MS) {
      h["X-Aktion-Id"] = letzteAktion.id;
      h["X-Aktion"] = encodeURIComponent(letzteAktion.name);
      if (!v && letzteAktion.vorgangId) h["X-Vorgang-Id"] = letzteAktion.vorgangId;
    }
  } catch { /* nie stören */ }
  return h;
}

// Seite geschlossen oder neu geladen: offene Vorgänge als „seite_verlassen“ abschließen.
// Zwei Wege: sendBeacon sofort, und zusätzlich ein Vermerk im Tab-Speicher, der beim nächsten Laden im
// selben Tab normal nachgesendet wird (im Test kam der Beacon beim Neuladen nicht zuverlässig an).
const NACHSENDEN = "ablauf_nachsenden";
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    try {
      const enden = [...offen].reverse().map((v) => ({ typ: "vorgang_ende", vorgang_id: v.id, name: v.name, ergebnis: "seite_verlassen", bezug: v.bezug }));
      if (enden.length) sessionStorage.setItem(NACHSENDEN, JSON.stringify(enden));
      [...offen].reverse().forEach((v) => beendeVorgang(v.id, "seite_verlassen", {}, { beimSchliessen: true }));
    } catch { /* nie stören */ }
  });
  try {
    const rest = JSON.parse(sessionStorage.getItem(NACHSENDEN) || "[]");
    sessionStorage.removeItem(NACHSENDEN);
    rest.slice(0, 20).forEach((e) => senden({ ...e, nachgesendet: true }));
  } catch { /* nie stören */ }
}
